import { retrieveRawInitData } from '@tma.js/sdk-react'
import type {
  PassportOcrData,
  PassportOcrPage,
  SupplierFieldKey,
} from '@/configs/docs-form'
import type { DocsFormValues } from '@/helpers/docs-form'

const apiBaseUrl = import.meta.env.BUN_PUBLIC_API_URL
  ? new URL(
      import.meta.env.BUN_PUBLIC_API_URL.endsWith('/')
        ? import.meta.env.BUN_PUBLIC_API_URL
        : `${import.meta.env.BUN_PUBLIC_API_URL}/`,
      window.location.origin,
    )
  : null

const passportOcrApiUrl = apiBaseUrl ? new URL('passport', apiBaseUrl) : null

const documentsApiUrl = apiBaseUrl ? new URL('documents', apiBaseUrl) : null
const meApiUrl = apiBaseUrl ? new URL('me', apiBaseUrl) : null
const referralApiUrl = apiBaseUrl ? new URL('referral', apiBaseUrl) : null

export type ApiTeam = {
  id: string
  name: string
  usdtSellRate: string
  usdtRateMarkupPercent: string
  usdtRateThresholdPercent: string | null
  autoUpdateRate: boolean
  rateUpdatedAt: string | null
}

export type ApiMe = {
  user: {
    id: string
    telegramUserId: number
    firstName: string | null
    lastName: string | null
    username: string | null
    role: 'admin' | 'manager' | 'user'
  }
  teams: ApiTeam[]
}

export type ApiReferralStats = {
  totalObligations: string
  totalPaid: string
  pending: string
  dealsCount: number
  referredUsersCount: number
  referralPercent: string
}

export type ApiReferralCode = {
  code: string
  label: string | null
  link: string | null
  text: string | null
}

export type ApiReferral =
  | { enabled: false }
  | {
      enabled: true
      stats: ApiReferralStats
      codes: ApiReferralCode[]
    }

export type ApiCbrRate = {
  rate: number
  updatedAt: string
}

export type ApiForexRate = {
  bid: number
  ask: number
  updatedAt: string
}

export type ApiTeamRate = ApiTeam

export type ApiDocumentRequestStatus =
  | 'created'
  | 'client_paid'
  | 'bank_received'
  | 'cancelled'

export type ApiFinancialEventType =
  | 'client_payment_confirmed'
  | 'client_usdt_obligation_created'
  | 'bank_payment_received'
  | 'usdt_transfer_sent'
  | 'manual_adjustment'
  | 'referral_obligation_created'
  | 'referral_payout_sent'

export type ApiDocumentRequest = {
  id: string
  teamId: string
  teamName: string
  createdByUserId: string
  createdByTelegramUserId: number
  contractNo: string
  invoiceNo: string | null
  invoiceAmount: string | null
  invoiceCurrency: string | null
  amountRub: string
  status: ApiDocumentRequestStatus
  clientPaidUsdtRate: string | null
  clientPaidUsdtAmount: string | null
  clientPaidAt: string | null
  bankReceivedAt: string | null
  cancelledAt: string | null
  updatedAt: string
  agencyOrderNo: number | null
  data: { fullNameRu: string; invoiceNo: string; [key: string]: string }
  createdAt: string
}

export type ApiFinancialEvent = {
  id: string
  type: ApiFinancialEventType
  rubAmount: string | null
  usdtAmount: string | null
  usdtRate: string | null
  createdByTelegramUserId: number | null
  occurredAt: string
  createdAt: string
}

export type ApiFinancialStats = {
  createdRequestsCount: number
  createdRequestsRub: string
  clientPaidCount: number
  clientPaidRub: string
  clientPaidUsdt: string
  rubInTransit: string
  rubReceived: string
  rubPaidAndReceived: string
  usdtToSend: string
  usdtSent: string
  avgRate: string | null
  minRate: string | null
  maxRate: string | null
}

export type ApiFinancialChartPoint = {
  date: string
  amountRub: string
  avgRate: string | null
}

export type ApiDocumentRequestDetails = {
  request: ApiDocumentRequest
  events: ApiFinancialEvent[]
}

export type ApiSupplier = {
  id: string
  teamId: string
  name: string
  description: string | null
  values: Pick<DocsFormValues, SupplierFieldKey>
}

export type ApiSupplierInput = {
  name: string
  description: string | null
  supplierName: string
  supplierAddress: string
  supplierAccount: string
  supplierBank: string
  supplierBankAddress: string
  supplierBankBranch: string
  supplierBankCode: string
}

const getErrorMessage = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const issues = 'issues' in payload ? payload.issues : null
  const issueMessages: string[] = []

  if (Array.isArray(issues)) {
    for (const issue of issues) {
      if (!issue || typeof issue !== 'object') {
        continue
      }

      const message = 'message' in issue ? issue.message : null
      if (typeof message === 'string' && message.trim()) {
        issueMessages.push(message.trim())
      }
    }
  }

  if (issueMessages.length > 0) {
    return issueMessages.join('\n')
  }

  const error = 'error' in payload ? payload.error : null
  if (typeof error === 'string' && error.trim()) {
    return error
  }

  return null
}

export const uploadPassportImage = async (
  page: PassportOcrPage,
  file: File,
) => {
  if (!passportOcrApiUrl)
    throw new Error('Не задан BUN_PUBLIC_API_URL для OCR API')

  const formData = new FormData()
  formData.append('page', page)
  formData.append('file', file)

  const response = await fetch(passportOcrApiUrl, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `tma ${retrieveRawInitData()}`,
    },
  })

  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось распознать изображение паспорта',
    )
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('OCR API вернул некорректный ответ')
  }

  return payload as PassportOcrData
}

export const fetchMe = async () => {
  if (!meApiUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(meApiUrl, {
    headers: {
      Authorization: `tma ${retrieveRawInitData()}`,
    },
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось загрузить профиль')
  }

  return payload as ApiMe
}

export const fetchReferral = async (): Promise<ApiReferral> => {
  if (!referralApiUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(referralApiUrl, {
    headers: authHeaders(),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось загрузить реферальную статистику',
    )
  }

  if (!payload || typeof payload !== 'object' || !('enabled' in payload)) {
    throw new Error('API реферальной программы вернул некорректный ответ')
  }

  return payload as ApiReferral
}

export const fetchTeamSuppliers = async (teamId: string) => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`teams/${teamId}/suppliers`, apiBaseUrl),
    {
      headers: {
        Authorization: `tma ${retrieveRawInitData()}`,
      },
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось загрузить поставщиков',
    )
  }

  if (!payload || typeof payload !== 'object' || !('suppliers' in payload)) {
    throw new Error('API поставщиков вернул некорректный ответ')
  }

  return payload as { suppliers: ApiSupplier[] }
}

export const createSupplier = async (
  teamId: string,
  input: ApiSupplierInput,
): Promise<ApiSupplier> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`teams/${teamId}/suppliers`, apiBaseUrl),
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось создать поставщика')
  }

  if (!payload || typeof payload !== 'object' || !('supplier' in payload)) {
    throw new Error('API поставщиков вернул некорректный ответ')
  }

  return (payload as { supplier: ApiSupplier }).supplier
}

export const updateSupplier = async (
  teamId: string,
  supplierId: string,
  input: ApiSupplierInput,
): Promise<ApiSupplier> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`teams/${teamId}/suppliers/${supplierId}`, apiBaseUrl),
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось сохранить поставщика',
    )
  }

  if (!payload || typeof payload !== 'object' || !('supplier' in payload)) {
    throw new Error('API поставщиков вернул некорректный ответ')
  }

  return (payload as { supplier: ApiSupplier }).supplier
}

export const deleteSupplier = async (
  teamId: string,
  supplierId: string,
): Promise<void> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`teams/${teamId}/suppliers/${supplierId}`, apiBaseUrl),
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось удалить поставщика')
  }
}

export const submitDocuments = async (
  values: DocsFormValues,
  teamId: string,
) => {
  if (!documentsApiUrl) {
    throw new Error('Не задан BUN_PUBLIC_API_URL для API документов')
  }

  const response = await fetch(documentsApiUrl, {
    method: 'POST',
    headers: {
      Authorization: `tma ${retrieveRawInitData()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...values, teamId }),
  })

  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось отправить данные формы',
    )
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('API документов вернул некорректный ответ')
  }

  return payload as { ok: true; contractNo: string }
}

export const updateDocumentRequest = async (
  requestId: string,
  values: DocsFormValues,
  teamId: string,
) => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`document-requests/${requestId}`, apiBaseUrl),
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...values, teamId }),
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось обновить заявку')
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('API заявки вернул некорректный ответ')
  }

  return payload as { ok: true; contractNo: string }
}

const authHeaders = () => ({
  Authorization: `tma ${retrieveRawInitData()}`,
})

export const fetchCbrRate = async (): Promise<ApiCbrRate | null> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL('rates/cbr', apiBaseUrl), {
    headers: authHeaders(),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (response.status === 404) return null

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось загрузить курс ЦБ')
  }

  if (!payload || typeof payload !== 'object' || !('rate' in payload)) {
    throw new Error('API курса ЦБ вернул некорректный ответ')
  }

  return (payload as { rate: ApiCbrRate }).rate
}

export const fetchForexRate = async (): Promise<ApiForexRate | null> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL('rates/forex', apiBaseUrl), {
    headers: authHeaders(),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (response.status === 404) return null

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось загрузить курс Forex',
    )
  }

  if (!payload || typeof payload !== 'object' || !('rate' in payload)) {
    throw new Error('API курса Forex вернул некорректный ответ')
  }

  return (payload as { rate: ApiForexRate }).rate
}

export const fetchTeamRate = async (teamId: string): Promise<ApiTeamRate> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL(`teams/${teamId}/rate`, apiBaseUrl), {
    headers: authHeaders(),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось загрузить курс компании',
    )
  }

  if (!payload || typeof payload !== 'object' || !('team' in payload)) {
    throw new Error('API курса компании вернул некорректный ответ')
  }

  return (payload as { team: ApiTeamRate }).team
}

export const updateTeamRate = async (
  teamId: string,
  data: {
    usdtSellRate: number
    usdtRateMarkupPercent: number
    usdtRateThresholdPercent: number | null
    autoUpdateRate: boolean
  },
): Promise<ApiTeamRate> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL(`teams/${teamId}/rate`, apiBaseUrl), {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось сохранить курс компании',
    )
  }

  if (!payload || typeof payload !== 'object' || !('team' in payload)) {
    throw new Error('API курса компании вернул некорректный ответ')
  }

  return (payload as { team: ApiTeamRate }).team
}

export type FetchDocumentRequestsParams = {
  status?: ApiDocumentRequestStatus
  agencyOrderNo?: number | 'null'
  teamId?: string
  limit?: number
  offset?: number
  search?: string
}

export const fetchDocumentRequests = async (
  params: FetchDocumentRequestsParams = {},
): Promise<{ requests: ApiDocumentRequest[]; hasMore: boolean }> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const url = new URL('document-requests', apiBaseUrl)
  if (params.status) url.searchParams.set('status', params.status)
  if (params.agencyOrderNo !== undefined) {
    url.searchParams.set('agencyOrderNo', String(params.agencyOrderNo))
  }
  if (params.teamId) url.searchParams.set('teamId', params.teamId)
  if (params.limit) url.searchParams.set('limit', String(params.limit))
  if (params.offset) url.searchParams.set('offset', String(params.offset))
  if (params.search) url.searchParams.set('search', params.search)

  const response = await fetch(url, { headers: authHeaders() })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось загрузить заявки')
  }

  if (!payload || typeof payload !== 'object' || !('requests' in payload)) {
    throw new Error('API заявок вернул некорректный ответ')
  }

  const result = payload as {
    requests: ApiDocumentRequest[]
    hasMore?: boolean
  }
  return { requests: result.requests, hasMore: result.hasMore === true }
}

export const fetchFinancialStats = async (params: {
  teamId?: string
  from: string
  to: string
}): Promise<{
  team: { id: string; name: string } | null
  stats: ApiFinancialStats
  chart: ApiFinancialChartPoint[]
}> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const url = new URL('stats', apiBaseUrl)
  if (params.teamId) url.searchParams.set('teamId', params.teamId)
  url.searchParams.set('from', params.from)
  url.searchParams.set('to', params.to)

  const response = await fetch(url, { headers: authHeaders() })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось загрузить статистику',
    )
  }

  if (
    !payload ||
    typeof payload !== 'object' ||
    !('stats' in payload) ||
    !('team' in payload)
  ) {
    throw new Error('API статистики вернул некорректный ответ')
  }

  return payload as {
    team: { id: string; name: string } | null
    stats: ApiFinancialStats
    chart: ApiFinancialChartPoint[]
  }
}

export const fetchDocumentRequest = async (
  id: string,
): Promise<ApiDocumentRequestDetails> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL(`document-requests/${id}`, apiBaseUrl), {
    headers: authHeaders(),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getErrorMessage(payload) ?? 'Не удалось загрузить заявку')
  }

  if (
    !payload ||
    typeof payload !== 'object' ||
    !('request' in payload) ||
    !('events' in payload)
  ) {
    throw new Error('API заявки вернул некорректный ответ')
  }

  return payload as ApiDocumentRequestDetails
}

export const regenerateDocumentRequest = async (id: string) => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(
    new URL(`document-requests/${id}/regenerate`, apiBaseUrl),
    {
      method: 'POST',
      headers: authHeaders(),
    },
  )
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось запустить генерацию документов',
    )
  }

  return payload as { ok: true; queued: true; id: string }
}

export const createAgencyOrder = async (params: {
  requestIds: string[]
  orderNo: number
  amountRub: number
  documentDate: string
}): Promise<{ orderNo: number }> => {
  if (!apiBaseUrl) throw new Error('Не задан BUN_PUBLIC_API_URL для API')

  const response = await fetch(new URL('agency-orders', apiBaseUrl), {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })
  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(
      getErrorMessage(payload) ?? 'Не удалось сформировать поручение',
    )
  }

  if (!payload || typeof payload !== 'object' || !('orderNo' in payload)) {
    throw new Error('API поручений вернул некорректный ответ')
  }

  return payload as { orderNo: number }
}
